import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Edit, GripVertical, CheckCircle } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuizManagementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testQuestions, setTestQuestions] = useState([]);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterTier, setFilterTier] = useState('all');
  const [filterSection, setFilterSection] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [testSearch, setTestSearch] = useState('');
  const [testSort, setTestSort] = useState('recent');
  const [questionSort, setQuestionSort] = useState('recent');
  
  // Test Categories Management
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesByTier, setCategoriesByTier] = useState({
    basic: [],
    comps_ready: [],
    advanced: []
  });

  useEffect(() => {
    loadTests();
    loadAvailableQuestions();
    loadCategories();
    
    // Check if testId is in URL params
    const testId = searchParams.get('testId');
    if (testId) {
      // Wait for tests to load, then select the one from URL
      loadTests().then(() => {
        // Find and select the test after load
        setTimeout(() => {
          const test = tests.find(t => t.id === testId);
          if (test) setSelectedTest(test);
        }, 100);
      });
    }
  }, []);

  useEffect(() => {
    if (selectedTest) {
      loadTestQuestions();
    }
  }, [selectedTest]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-categories`, {
        withCredentials: true
      });
      setAllCategories(response.data);
      
      // Organize categories by tier
      const byTier = {
        basic: [],
        comps_ready: [],
        advanced: []
      };
      
      response.data.forEach(cat => {
        if (cat.enabled && byTier[cat.tier]) {
          byTier[cat.tier].push({
            value: cat.slug,
            label: cat.name
          });
        }
      });
      
      setCategoriesByTier(byTier);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load test categories');
    }
  };
  
  // Helper to get section label from slug
  const getSectionLabelFromSlug = (slug) => {
    for (const tier of ['basic', 'comps_ready', 'advanced']) {
      const found = categoriesByTier[tier]?.find(cat => cat.value === slug);
      if (found) return found.label;
    }
    return slug; // Fallback to slug if not found
  };

  const loadTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`, {
        withCredentials: true
      });
      setTests(response.data);
      return response.data;
    } catch (error) {
      toast.error('Failed to load tests');
    }
  };

  const loadTestQuestions = async () => {
    if (!selectedTest) return;
    
    try {
      const response = await axios.get(`${API}/tests/${selectedTest.id}/questions`, {
        withCredentials: true
      });
      setTestQuestions(response.data);
    } catch (error) {
      toast.error('Failed to load test questions');
    }
  };

  const loadAvailableQuestions = async () => {
    try {
      const response = await axios.get(`${API}/admin/question-pool`, {
        withCredentials: true
      });
      setAvailableQuestions(response.data);
    } catch (error) {
      toast.error('Failed to load available questions');
    }
  };

  const handleAddQuestions = async () => {
    if (selectedQuestionIds.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/add-questions-to-test/${selectedTest.id}`,
        selectedQuestionIds,
        { withCredentials: true }
      );
      toast.success(`Added ${selectedQuestionIds.length} questions to test`);
      setSelectedQuestionIds([]);
      loadTestQuestions();
      loadTests(); // Refresh to update question count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add questions');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    if (!window.confirm('Remove this question from the test?')) return;

    try {
      await axios.delete(`${API}/admin/tests/${selectedTest.id}/questions/${questionId}`, {
        withCredentials: true
      });
      toast.success('Question removed from test');
      loadTestQuestions();
      loadTests(); // Refresh to update question count
    } catch (error) {
      toast.error('Failed to remove question');
    }
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestionIds(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredAvailableQuestions.map(q => q.id);
    setSelectedQuestionIds(prev => {
      // If all filtered questions are already selected, deselect them
      const allSelected = allFilteredIds.every(id => prev.includes(id));
      if (allSelected) {
        // Remove all filtered IDs from selection
        return prev.filter(id => !allFilteredIds.includes(id));
      } else {
        // Add all filtered IDs that aren't already selected
        const newIds = allFilteredIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      }
    });
  };

  const handleClearAll = () => {
    setSelectedQuestionIds([]);
  };

  const filteredAvailableQuestions = availableQuestions.filter(q => {
    // Exclude questions already in the test
    if (testQuestions.some(tq => tq.id === q.id)) return false;
    
    // Filter by tier: get all sections for the tier
    if (filterTier !== 'all') {
      const tierSections = categoriesByTier[filterTier] || [];
      const tierSectionSlugs = tierSections.map(s => s.value);
      if (!tierSectionSlugs.includes(q.category)) return false;
    }
    
    // Filter by section
    if (filterSection !== 'all' && q.category !== filterSection) return false;
    
    // Filter by creation date
    if (filterDate !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      const hourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      
      const createdDate = new Date(q.created_at);
      switch (filterDate) {
        case 'hour':
          if (createdDate < hourAgo) return false;
          break;
        case 'today':
          if (createdDate < today) return false;
          break;
        case 'yesterday':
          if (createdDate < yesterday || createdDate >= today) return false;
          break;
        case 'week':
          if (createdDate < weekAgo) return false;
          break;
        case 'month':
          if (createdDate < monthAgo) return false;
          break;
        default:
          break;
      }
    }
    
    return true;
  }).sort((a, b) => (
    questionSort === 'oldest'
      ? new Date(a.created_at) - new Date(b.created_at)
      : new Date(b.created_at) - new Date(a.created_at)  // most recent first (default)
  ));

  const displayedTests = [...tests]
    .filter(t => !testSearch.trim() || (t.title || '').toLowerCase().includes(testSearch.toLowerCase()))
    .sort((a, b) => {
      if (testSort === 'title') return (a.title || '').localeCompare(b.title || '');
      if (testSort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return new Date(b.created_at) - new Date(a.created_at); // recent (default)
    });

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8B67A] py-8">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Header */}
          <div className="cassette-card p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/admin')}
                  className="retro-button-small flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Admin
                </button>
                <h1 className="text-4xl retro-title text-[#2C1810]">Test Question Management</h1>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left: Test List */}
            <div className="lg:col-span-1">
              <div className="cassette-card p-6">
                <h2 className="text-xl font-bold text-[#2C1810] mb-4">Select Test</h2>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <input
                    type="text"
                    value={testSearch}
                    onChange={(e) => setTestSearch(e.target.value)}
                    placeholder="Search tests..."
                    data-testid="select-test-search"
                    className="flex-1 px-3 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810] text-sm"
                  />
                  <select
                    value={testSort}
                    onChange={(e) => setTestSort(e.target.value)}
                    data-testid="select-test-sort"
                    className="px-3 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810] text-sm font-semibold"
                  >
                    <option value="recent">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="title">Title (A–Z)</option>
                  </select>
                </div>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {displayedTests.length === 0 ? (
                    <p className="text-[#6B4423] text-sm">{tests.length === 0 ? 'No tests found' : 'No tests match your search'}</p>
                  ) : (
                    displayedTests.map(test => (
                      <button
                        key={test.id}
                        onClick={() => setSelectedTest(test)}
                        className={`w-full text-left p-4 rounded-lg transition-colors ${
                          selectedTest?.id === test.id
                            ? 'bg-[#E8934D] text-white'
                            : 'bg-[#F4E4C1] hover:bg-[#E8B67A] text-[#2C1810]'
                        }`}
                      >
                        <p className="font-bold">{test.title}</p>
                        <p className="text-xs opacity-80">
                          Pass: {test.passing_score}% | Questions: {test.question_count || 0}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right: Question Management */}
            <div className="lg:col-span-2">
              {!selectedTest ? (
                <div className="cassette-card p-12 text-center">
                  <p className="text-[#6B4423] text-lg">Select a test to manage its questions</p>
                </div>
              ) : (
                <div className="space-y-6">
                  
                  {/* Current Questions in Test */}
                  <div className="cassette-card p-6">
                    <h2 className="text-xl font-bold text-[#2C1810] mb-4">
                      Questions in &quot;{selectedTest.title}&quot; ({testQuestions.length})
                    </h2>
                    
                    {testQuestions.length === 0 ? (
                      <p className="text-[#6B4423] text-center py-8">No questions added yet</p>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {testQuestions.map((question, index) => (
                          <div key={question.id} className="bg-[#F4E4C1] p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <span className="font-bold text-[#2C1810] mt-1">{index + 1}.</span>
                              <div className="flex-1">
                                <p className="text-[#2C1810] mb-2">{question.question_text}</p>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="px-2 py-1 bg-[#E8934D] text-white rounded">
                                    {question.level}
                                  </span>
                                  <span className="px-2 py-1 bg-[#4D9E7C] text-white rounded">
                                    {getSectionLabelFromSlug(question.category)}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveQuestion(question.id)}
                                className="p-2 hover:bg-red-200 rounded-lg transition-colors"
                                title="Remove from test"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Questions Section */}
                  <div className="cassette-card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-[#2C1810]">
                        Add Questions ({selectedQuestionIds.length} selected)
                      </h2>
                      {selectedQuestionIds.length > 0 && (
                        <button
                          onClick={handleAddQuestions}
                          disabled={loading}
                          className="retro-button flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          {loading ? 'Adding...' : 'Add Selected'}
                        </button>
                      )}
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                      <div>
                        <label className="block text-sm font-bold text-[#2C1810] mb-1">Tier</label>
                        <select
                          value={filterTier}
                          onChange={(e) => {
                            setFilterTier(e.target.value);
                            setFilterSection('all'); // Reset section when tier changes
                          }}
                          className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                        >
                          <option value="all">All Tiers</option>
                          <option value="basic">Basic Practice</option>
                          <option value="comps_ready">Comps Ready</option>
                          <option value="advanced">Advanced</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2C1810] mb-1">Section</label>
                        <select
                          value={filterSection}
                          onChange={(e) => setFilterSection(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                        >
                          <option value="all">All Sections</option>
                          {filterTier !== 'all' ? (
                            // Show sections for selected tier
                            categoriesByTier[filterTier]?.map(section => (
                              <option key={section.value} value={section.value}>{section.label}</option>
                            ))
                          ) : (
                            // Show all sections organized by tier
                            <>
                              <optgroup label="Basic Practice">
                                {categoriesByTier.basic?.map(section => (
                                  <option key={section.value} value={section.value}>{section.label}</option>
                                ))}
                              </optgroup>
                              <optgroup label="Comps Ready">
                                {categoriesByTier.comps_ready?.map(section => (
                                  <option key={section.value} value={section.value}>{section.label}</option>
                                ))}
                              </optgroup>
                              <optgroup label="Advanced">
                                {categoriesByTier.advanced?.map(section => (
                                  <option key={section.value} value={section.value}>{section.label}</option>
                                ))}
                              </optgroup>
                            </>
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2C1810] mb-1">Created</label>
                        <select
                          value={filterDate}
                          onChange={(e) => setFilterDate(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                        >
                          <option value="all">All Dates</option>
                          <option value="hour">Last Hour</option>
                          <option value="today">Today</option>
                          <option value="yesterday">Yesterday</option>
                          <option value="week">This Week</option>
                          <option value="month">This Month</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2C1810] mb-1">Sort</label>
                        <select
                          value={questionSort}
                          onChange={(e) => setQuestionSort(e.target.value)}
                          data-testid="add-questions-sort"
                          className="w-full px-3 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
                        >
                          <option value="recent">Most Recent</option>
                          <option value="oldest">Oldest</option>
                        </select>
                      </div>
                    </div>

                    {/* Select All Controls */}
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        onClick={handleSelectAll}
                        className="px-4 py-2 bg-[#4D9E7C] text-white rounded font-bold text-sm hover:bg-[#3D7A5F] transition-colors"
                      >
                        {filteredAvailableQuestions.length > 0 && 
                         filteredAvailableQuestions.every(q => selectedQuestionIds.includes(q.id))
                          ? 'Deselect All' 
                          : 'Select All'} 
                        ({filteredAvailableQuestions.length})
                      </button>
                      {selectedQuestionIds.length > 0 && (
                        <button
                          onClick={handleClearAll}
                          className="px-4 py-2 bg-[#E85D4D] text-white rounded font-bold text-sm hover:bg-[#C44136] transition-colors"
                        >
                          Clear All
                        </button>
                      )}
                      <span className="text-sm text-[#6B4423]">
                        {selectedQuestionIds.length} of {availableQuestions.filter(q => !testQuestions.some(tq => tq.id === q.id)).length} questions selected
                      </span>
                    </div>

                    {/* Available Questions */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {filteredAvailableQuestions.length === 0 ? (
                        <p className="text-[#6B4423] text-center py-8">
                          {availableQuestions.length === 0 
                            ? 'No questions in question bank'
                            : 'No questions match filters (or all are already in test)'}
                        </p>
                      ) : (
                        filteredAvailableQuestions.map(question => (
                          <div
                            key={question.id}
                            onClick={() => toggleQuestionSelection(question.id)}
                            className={`p-4 rounded-lg cursor-pointer transition-colors ${
                              selectedQuestionIds.includes(question.id)
                                ? 'bg-[#4D9E7C] text-white'
                                : 'bg-white hover:bg-[#F4E4C1]'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-1">
                                {selectedQuestionIds.includes(question.id) ? (
                                  <CheckCircle className="w-5 h-5" />
                                ) : (
                                  <div className="w-5 h-5 border-2 border-current rounded" />
                                )}
                              </div>
                              <div className="flex-1">
                                <p className={`mb-2 ${selectedQuestionIds.includes(question.id) ? 'text-white' : 'text-[#2C1810]'}`}>
                                  {question.question_text}
                                </p>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={`px-2 py-1 rounded ${
                                    selectedQuestionIds.includes(question.id)
                                      ? 'bg-white/20'
                                      : 'bg-[#E8934D] text-white'
                                  }`}>
                                    {question.level}
                                  </span>
                                  <span className={`px-2 py-1 rounded ${
                                    selectedQuestionIds.includes(question.id)
                                      ? 'bg-white/20'
                                      : 'bg-[#4D9E7C] text-white'
                                  }`}>
                                    {getSectionLabelFromSlug(question.category)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                </div>
              )}
            </div>

          </div>

        </div>
      </div>
    </Layout>
  );
}
